import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  MessageCircle, X, Send, Loader2, Minimize2, Maximize2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [guestInfo, setGuestInfo] = useState({ name: '', email: '' });
  const [showGuestForm, setShowGuestForm] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  // Initialize session from localStorage
  useEffect(() => {
    const storedSessionId = localStorage.getItem('chat_session_id');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  // Scroll to bottom only when new messages arrive
  const prevMessageCount = useRef(0);
  useEffect(() => {
    // Only scroll if there are new messages (not on every re-render)
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCount.current = messages.length;
  }, [messages]);

  // Poll for new messages when chat is open
  useEffect(() => {
    if (isOpen && sessionId && !isMinimized) {
      fetchMessages();
      pollInterval.current = setInterval(fetchMessages, 5000);
    }
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [isOpen, sessionId, isMinimized]);

  const fetchMessages = async () => {
    if (!sessionId) return;
    
    try {
      const headers = {};
      if (isAuthenticated) {
        headers.Authorization = `Bearer ${localStorage.getItem('auth_token')}`;
      }
      
      const response = await axios.get(`${API}/chat/messages/${sessionId}`, { headers });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    
    // Show guest form if not authenticated and no session
    if (!isAuthenticated && !sessionId) {
      setShowGuestForm(true);
    } else if (sessionId) {
      fetchMessages();
    }
  };

  const handleStartChat = async () => {
    if (!isAuthenticated && (!guestInfo.name.trim() || !guestInfo.email.trim())) {
      toast.error('Please enter your name and email');
      return;
    }
    
    setShowGuestForm(false);
    
    // Add welcome message
    setMessages([{
      message_id: 'welcome',
      sender: 'admin',
      sender_name: 'Support',
      message: `Hi ${isAuthenticated ? user?.name?.split(' ')[0] : guestInfo.name}! 👋 How can we help you today?`,
      timestamp: new Date().toISOString()
    }]);
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      const headers = {};
      if (isAuthenticated) {
        headers.Authorization = `Bearer ${localStorage.getItem('auth_token')}`;
      }
      
      const response = await axios.post(`${API}/chat/messages`, {
        message: newMessage.trim(),
        session_id: sessionId,
        customer_name: isAuthenticated ? user?.name : guestInfo.name,
        customer_email: isAuthenticated ? user?.email : guestInfo.email
      }, { headers });
      
      // Store session ID
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        localStorage.setItem('chat_session_id', response.data.session_id);
      }
      
      // Add message to local state immediately
      setMessages(prev => [...prev, {
        message_id: response.data.message_id,
        sender: 'customer',
        sender_name: isAuthenticated ? user?.name : guestInfo.name,
        message: newMessage.trim(),
        timestamp: new Date().toISOString()
      }]);
      
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="fixed bottom-20 right-6 z-50 bg-[#CCFF00] text-black p-4 rounded-full shadow-lg hover:bg-[#B3E600] transition-all hover:scale-110"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-20 right-6 z-50 bg-[#111] border border-white/10 rounded-lg shadow-xl transition-all ${
        isMinimized ? 'w-72 h-14' : 'w-80 sm:w-96 h-[500px]'
      }`}
    >
      {/* Header */}
      <div className="bg-[#1a1a1a] px-4 py-3 rounded-t-lg flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-white font-medium">Support Chat</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-white/50 hover:text-white p-1"
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/50 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Guest Form */}
          {showGuestForm ? (
            <div className="p-4 space-y-4 flex-1">
              <div className="text-center mb-6">
                <MessageCircle className="w-12 h-12 text-[#CCFF00] mx-auto mb-3" />
                <h3 className="text-white font-bold text-lg">Start a Conversation</h3>
                <p className="text-white/50 text-sm">Enter your details to begin</p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Input
                    value={guestInfo.name}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your Name"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    value={guestInfo.email}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Your Email"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <Button
                  onClick={handleStartChat}
                  className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
                >
                  Start Chat
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ height: '380px' }}>
                {messages.length === 0 && !loading && (
                  <div className="text-center py-8">
                    <MessageCircle className="w-10 h-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/50 text-sm">Start the conversation!</p>
                  </div>
                )}
                
                {messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 ${
                        msg.sender === 'customer'
                          ? 'bg-[#CCFF00] text-black'
                          : 'bg-white/10 text-white'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender === 'customer' ? 'text-black/50' : 'text-white/30'
                      }`}>
                        {new Date(msg.timestamp).toLocaleTimeString('en-AU', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-white/10">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="bg-white/5 border-white/10 text-white flex-1"
                    disabled={sending}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] px-3"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ChatWidget;
